import { ChangeEvent, FunctionComponent, useCallback, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { zodResolver } from "@hookform/resolvers/zod";

import { useForm } from "react-hook-form";

import { AutoCompleteItem, Dropdown } from "@/components/Dropdown/Dropdown";

import { Modal } from "@/components";
import { InputField } from "@/components/InputField/InputField";

import { AiApplyType, CustomColumnTypes, PriorityFilterStatus, UpdateType } from "@/enum/list.enum";
import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";
import { ColumnConfig } from "@/pages/Private/pages/List/schema/columnConfig";

import { useUpdateListColumnsMutation } from "@/pages/Private/redux/list/list.api";

import { listSelector } from "@/pages/Private/redux/list/list.slice";
import { useAppSelector } from "@/redux/hooks";

import { CustomColumn, CustomColumnSchema } from "../../../schema/list";
import { CustomColumnApplyFilters } from "../customColumnApplyFilters";

export interface TextValueModalProps {
	setShowConfirm: (value: boolean) => void;
	open: boolean;
	listId: number;
	setOpen: (value: boolean) => void;
	columnId?: number;
	column?: ColumnConfig;
	customColumnName?: string;
	filters?: CustomColumnApplyFilters;
	selectedProspectIds?: number[];
	priorityFilterStatus?: PriorityFilterStatus[];
}

export const TextValueModal: FunctionComponent<TextValueModalProps> = ({
	setShowConfirm,
	listId,
	open,
	setOpen,
	columnId,
	column,
	customColumnName,
	filters,
	selectedProspectIds,
	priorityFilterStatus,
}) => {
	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`aiColumn.${key}`), [t]);

	const [textValue, setTextValue] = useState<string>(
		column?.config ? (column.config as string) : ""
	);

	const [update, { isLoading, error }] = useUpdateListColumnsMutation();
	const [applyTo, setApplyTo] = useState<AiApplyType>(AiApplyType.NONE);

	const { inbox } = useAppSelector(listSelector);

	const {
		handleSubmit,
		setValue,
		formState: { errors },
		watch,
	} = useForm<CustomColumn>({
		defaultValues: {
			customColumnType: CustomColumnTypes.TEXT,
			...column,
			customColumnName: customColumnName || column?.name,
		},
		resolver: zodResolver(CustomColumnSchema),
	});

	if (Object.values(errors).length) {
		console.log(errors);
	}

	const formValues = watch();

	const onSubmit = async (values: CustomColumn) => {
		try {
			values.value = textValue;

			const prospectIds =
				applyTo === AiApplyType.FIRST_10
					? inbox.slice(0, 10).map((p) => p.id)
					: applyTo === AiApplyType.CURRENTLY_SELECTED
						? selectedProspectIds
						: undefined;

			await update({
				...values,
				id: listId,
				columnId,
				updateType: UpdateType.CUSTOM_COLUMN,
				aiApplyType: applyTo,
				prospectIds: prospectIds,
				filters: filters,
				priorityFilterStatus: priorityFilterStatus ?? undefined,
			}).unwrap();

			setOpen(false);
		} catch (err) {
			console.error(err);
		}
	};

	const applyToButtonText = useMemo(() => {
		return applyTo === AiApplyType.NONE
			? ts(`button.${AiApplyType.NONE}`)
			: applyTo === AiApplyType.FIRST_10
				? ts(`button.${AiApplyType.FIRST_10}`)
				: applyTo === AiApplyType.CURRENTLY_FILTERED
					? ts(`button.${AiApplyType.CURRENTLY_FILTERED}`)
					: ts(`button.${AiApplyType.ALL}`);
	}, [applyTo, ts]);

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];

	return (
		<Modal
			disableSave={Object.values(errors).length > 0 || !formValues.customColumnName}
			handleClose={() => {
				setShowConfirm(true);
			}}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isLoading}
			isOpened={open}
			size="md"
			submitButtonText={applyToButtonText}
			title={column ? "Edit Text Value Column" : "Create Text Value Column"}
		>
			<div className="text-ssm font-medium mb-4">
				<InputField
					error={!!errors.customColumnName}
					handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
						setValue("customColumnName", event.target.value, {
							shouldValidate: true,
							shouldDirty: true,
						});
					}}
					label="Column Name"
					name={"name"}
					placeholder="Enter column name"
					value={formValues.customColumnName}
				/>
			</div>

			<div className="flex flex-row items-start space-y-4">
				<InputField
					className="!mb-0 grow mr-4 h-auto !field-sizing-content"
					error={!!errors.value}
					handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
						setTextValue(event.target.value);
					}}
					multiline={true}
					name={"value"}
					placeholder="Default value"
					value={textValue ?? ""}
				/>
			</div>

			<div className="flex flex-row mt-5 border-t pt-5 border-gray-200">
				<Dropdown
					data={[
						{
							title: ts(AiApplyType.NONE),
							id: AiApplyType.NONE,
						},
						{
							title: ts(AiApplyType.FIRST_10),
							id: AiApplyType.FIRST_10,
						},
						{
							title: ts(AiApplyType.CURRENTLY_FILTERED),
							id: AiApplyType.CURRENTLY_FILTERED,
						},
						{
							title: ts(AiApplyType.CURRENTLY_SELECTED),
							id: AiApplyType.CURRENTLY_SELECTED,
						},
						{
							title: ts(AiApplyType.ALL),
							id: AiApplyType.ALL,
						},
						{
							title: ts(AiApplyType.ALL_INCLUDING_ASSIGNED),
							id: AiApplyType.ALL_INCLUDING_ASSIGNED,
						},
					]}
					floating={false}
					handleSelect={function (value?: AutoCompleteItem) {
						if (value?.id) {
							setApplyTo(value.id as AiApplyType);
						}
					}}
					label="Apply to ..."
					value={applyTo ? { title: ts(applyTo), id: applyTo } : undefined}
				/>
			</div>

			{getAllErrors(error, formErrors).length ? (
				<div className="mt-4 mb-0">{renderErrorMessages(getAllErrors(error, formErrors))}</div>
			) : null}
		</Modal>
	);
};
